import type { UserDetail } from '@planuze/admin-contracts';
import {
  Button,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { Building2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useUsersContent } from '../../../content/sections/users.js';
import { fmtDate } from '../../../lib/format.js';
import { SearchInput } from '../../search-input.js';

type Org = UserDetail['orgs'][number];

/* A busca por nome só aparece quando a lista é grande o suficiente para justificar — listas
   curtas escaneiam melhor sem toolbar (o brief permite pular o caso trivial). */
const SEARCH_MIN_ORGS = 8;

export function OrgsTab({ orgs }: { readonly orgs: readonly Org[] }) {
  const copy = useUsersContent();
  const [query, setQuery] = useState('');
  const showSearch = orgs.length >= SEARCH_MIN_ORGS;
  const normalized = query.trim().toLowerCase();

  const rows = useMemo(
    () =>
      normalized === ''
        ? orgs
        : orgs.filter((org) => org.orgName.toLowerCase().includes(normalized)),
    [orgs, normalized],
  );

  if (orgs.length === 0) {
    return <EmptyState icon={<Building2 className="size-6" />} title={copy.orgsTab.empty} />;
  }

  return (
    <div className="space-y-3">
      {showSearch && (
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={query}
            onChange={setQuery}
            ariaLabel={copy.orgsTab.searchAriaLabel}
            placeholder={copy.orgsTab.searchPlaceholder}
          />
          {normalized !== '' && (
            <span className="text-sm text-muted-foreground tabular-nums">
              {copy.common.countOf(rows.length, orgs.length)}
            </span>
          )}
        </div>
      )}
      {rows.length === 0 ? (
        <EmptyState
          icon={<Building2 className="size-6" />}
          title={copy.common.noResultsTitle}
          description={copy.orgsTab.emptyFilteredDescription}
          primaryAction={
            <Button variant="outline" onClick={() => setQuery('')}>
              {copy.common.clearFilters}
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{copy.orgsTab.columnOrg}</TableHead>
              <TableHead>{copy.orgsTab.columnRole}</TableHead>
              <TableHead>{copy.orgsTab.columnJoinedAt}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((org) => (
              <TableRow key={org.orgId}>
                <TableCell className="text-foreground">{org.orgName}</TableCell>
                <TableCell className="text-muted-foreground">{org.role}</TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(org.joinedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
